import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { ApplicationTypeEnum } from '../../../legacy/shared-types';
import { Button, FormControl } from 'components/common';
import { IIntroductionForm } from './types';
import { ApplicationFormPageEnum } from '../types';
import { introductionQuestions } from './questions';
import { useNavigateBack } from 'hooks';
import { useEffect } from 'react';
import { parseApplicationInProgress } from '../helpers';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from 'api/applicationForm';
import { getCurrentRoute } from 'utils/location';

export const Introduction = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });
  const form = useForm<IIntroductionForm>({
    defaultValues: {
      purchaseType: '',
    },
  });

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const submitFormHandler = async (formData: IIntroductionForm) => {
    const deleteFollowingPages = formattedResponse && formattedResponse.purchaseType !== formData.purchaseType;
    const questions = Object.entries(formData).map(([key, answer]) => ({
      question: intl.formatMessage({ id: introductionQuestions[key].labelKey }),
      key,
      answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId, deleteFollowingPages });

    const sectionToRedirect =
      formData.purchaseType === ApplicationTypeEnum.PURCHASE
        ? ApplicationFormPageEnum.PURCHASE
        : ApplicationFormPageEnum.REFINANCING;

    searchParams.set('section', sectionToRedirect);
    setSearchParams(searchParams);
  };

  const isAnyFieldWithError = Boolean(Object.values(form.formState.errors).length);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <div className='mt-5'>
          <FormattedMessage id={introductionQuestions.purchaseType.labelKey} tagName={'h4'} />
          <FormControl
            rules={{ required: requiredFieldErrorMessage }}
            name={'purchaseType'}
            radioOptions={introductionQuestions.purchaseType.options}
            radioGroupProps={{ className: 'mt-2' }}
          />
        </div>

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>
          <Button variant={'contained'} type={'submit'} disabled={isAnyFieldWithError}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
